/**
 * Simple messaging service client to replace MessagingServiceClient from messaging-common-js
 * Only implements the logging endpoints that we actually use.
 */
import {
    MessagingServiceClientOptions,
    IMessagingServiceClient,
    UserActionLog,
    ImpressionLog,
    HttpClientResponse,
} from '../../types/messaging-types';
import { SimpleHttpClient } from './SimpleHttpClient';

/**
 * Convert camelCase or lowercase strings to SCREAMING_SNAKE_CASE
 * Examples:
 *   "jobDetail" -> "JOB_DETAIL"
 *   "conversations" -> "CONVERSATIONS"
 *   "aiAssistedMessaging" -> "AI_ASSISTED_MESSAGING"
 */
function toScreamingSnakeCase(str: string): string {
    return str
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // Add underscore before capital letters
        .toUpperCase();
}

export class SimpleMessagingServiceClient implements IMessagingServiceClient {
    private readonly httpClient: SimpleHttpClient;
    private readonly queryParams: string;

    constructor(options: MessagingServiceClientOptions) {
        // Ensure country code is never empty - use 'US' as fallback
        const country = options.LOCALE_COUNTRY?.trim() || 'US';
        const language = options.LOCALE_LANGUAGE?.trim() || 'en';

        const params = new URLSearchParams({
            clientApp: options.CLIENT_APP || 'MESSAGES_WEB',
            hl: language,
            co: country,
            pageLoadTk: options.PAGELOAD_TK || '',
        });
        this.queryParams = params.toString();

        this.httpClient = new SimpleHttpClient({
            baseURL: options.BASE_URL,
            timeout: options.DEFAULT_TIMEOUT || 10000,
            headers: {
                'X-Client-App': options.CLIENT_APP || 'MESSAGES_WEB',
                'Accept-Language': `${language}-${country}`,
            },
            // Use Axios's built-in CSRF protection
            xsrfCookieName: options.XSRF_COOKIE_NAME,
            xsrfHeaderName: options.XSRF_HEADER_NAME,
        });
    }

    public logging = {
        v1: {
            postUserActionLog: async (
                pageLoadTk: string,
                log: UserActionLog
            ): Promise<HttpClientResponse> => {
                // Convert enum values to SCREAMING_SNAKE_CASE for server compatibility
                const payload = {
                    pageLoadTk,
                    ...log,
                    itemType:
                        typeof log.itemType === 'string'
                            ? toScreamingSnakeCase(log.itemType)
                            : log.itemType,
                    action:
                        log.action && typeof log.action === 'string'
                            ? toScreamingSnakeCase(log.action)
                            : log.action,
                };
                return this.httpClient.post(
                    `/messaging/jobseekers/logging/v1/userActions?${this.queryParams}`,
                    payload
                );
            },

            postImpressionLog: async (
                pageLoadTk: string,
                log: ImpressionLog
            ): Promise<HttpClientResponse> => {
                // Convert enum values to SCREAMING_SNAKE_CASE for server compatibility
                const payload = {
                    pageLoadTk,
                    ...log,
                    itemType:
                        typeof log.itemType === 'string'
                            ? toScreamingSnakeCase(log.itemType)
                            : log.itemType,
                };
                return this.httpClient.post(
                    `/messaging/jobseekers/logging/v1/impressions?${this.queryParams}`,
                    payload
                );
            },
        },
    };

    // Add a method to get the HTTP client if needed for other uses
    public getHttpClient(): SimpleHttpClient {
        return this.httpClient;
    }
}
